import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Image,
  Grid,
  Box,
  List,
  ThemeIcon,
  Center,
  Flex
} from '@mantine/core';
import {
  IconCalendarWeek,
  IconChartBar,
  IconCheck,
  IconChefHat,
  IconCoin,
  IconMoodSad,
  IconPackage,
  IconSearch,
  IconShoppingCart,
  IconTrash
} from '@tabler/icons-react';
import { AuthButton } from '../AuthButton';
import { GradientText } from '../UI/GradientText';
import { FadeIn } from '../UI/FadeIn';
import { HeroRecipeShowcase } from './HeroRecipeShowcase';
import { SocialProof } from './SocialProof';
import { heroFontFamily } from '../../theme';
import { usePageTitle } from '../../hooks/usePageTitle';


const heroGridStyles = `
  .hero-col {
    flex: 0 0 100%;
    max-width: 100%;
  }
  .hero-desktop-cta { display: none; }
  .hero-mobile-cta { display: flex; }
  .hero-tablet-cta { display: none; }
  .hero-trial-mobile { display: flex; }
  .hero-trial-desktop { display: none; }
  .hero-grocery-desktop { display: none; }
  .hero-grocery-mobile { display: flex; }
  @media (max-width: 889px) {
    .hero-quote { text-align: center; }
  }
  @media (max-width: 400px) {
    .hero-quote-attribution { display: block; }
    /* Keep the offset collage image inside the column: its width gives
       back the 16px the offset adds, so the right edge can't overflow */
    .recipe-overlap { margin-left: 16px !important; max-width: calc(100% - 16px) !important; }
  }
  @media (min-width: 475px) and (max-width: 889px) {
    .hero-mobile-cta { display: none; }
    .hero-tablet-cta { display: flex; }
  }
  @media (min-width: 890px) {
    .hero-col {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .hero-desktop-cta { display: flex; }
    .hero-mobile-cta { display: none; }
    .hero-trial-mobile { display: none; }
    .hero-trial-desktop { display: flex; }
    .hero-grocery-desktop { display: flex; }
    .hero-grocery-mobile { display: none; }
  }
`;

export const LandingPage = () => {
  // Matches the default <title> in public/index.html so client-side navigation
  // back to the landing page restores the canonical title.
  usePageTitle('Meals You Love - Meal plans built around your life');
  return (
    <Box>
      <style>{heroGridStyles}</style>
      {/* ──────────────────────────────────────────────
          SECTION 1: HERO
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Grid align="flex-start" gutter="xl">
          <Grid.Col className="hero-col">
            <Stack gap="xl">
              <Title
                order={1}
                c="dark"
                style={{ fontFamily: heroFontFamily }}
              >
                Meal plans built around{' '}
                <GradientText>your life</GradientText>
              </Title>

              <Text size="lg" c="dimmed" lh={1.6}>
                Tell us your dietary needs, schedule, and preferences —
                we'll plan your whole week of dinners around them.
              </Text>

              <Stack gap="sm">
                <Flex gap="sm" align="flex-start">
                  <IconCheck size={15} stroke={2.4} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 4 }} />
                  <Text c="gray.8">Eat better without the mental load</Text>
                </Flex>
                <Flex gap="sm" align="flex-start">
                  <IconCheck size={15} stroke={2.4} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 4 }} />
                  <Text c="gray.8">Every meal checks every box</Text>
                </Flex>
                <Flex gap="sm" align="flex-start">
                  <IconCheck size={15} stroke={2.4} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 4 }} />
                  <Text c="gray.8">Just $9.99/month, a fraction of what meal kits cost</Text>
                </Flex>
              </Stack>

              <Text size="sm" c="dimmed" fs="italic" className="hero-quote">
                "It has saved me so much time and money." <span className="hero-quote-attribution">— Jennifer C.</span>
              </Text>

              {/* Mobile CTA (below 792px) */}
              <Group gap="md" className="hero-mobile-cta" style={{ flexDirection: 'column' }}>
                <AuthButton size="lg" fullWidth>
                  Build your plan for free
                </AuthButton>
              </Group>
              {/* Tablet CTA (475px-889px) */}
              <Group gap="md" justify="center" className="hero-tablet-cta">
                <AuthButton size="md">
                  Build your plan for free
                </AuthButton>
              </Group>
              {/* Desktop CTA (890px+) */}
              <Group gap="md" className="hero-desktop-cta">
                <AuthButton size="lg">
                  Build your plan for free
                </AuthButton>
              </Group>

              <Group gap="xs" justify="center" className="hero-trial-mobile">
                <Text size="sm" c="dimmed">
                  1-month free trial • No credit card required
                </Text>
              </Group>
              <Group gap="xs" className="hero-trial-desktop">
                <Text size="sm" c="dimmed">
                  1-month free trial • No credit card required
                </Text>
              </Group>

              <Group gap="sm" align="center" mt="sm" className="hero-grocery-desktop">
                <Text size="xs" c="dimmed" fw={500}>Works with</Text>
                <Image src="/images/kroger_integration_logo.svg" alt="Kroger" h={28} w="auto" />
                <Image src="/images/instacart_logo_large.png" alt="Instacart" h={20} w="auto" />
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col className="hero-col">
            <Center>
              <HeroRecipeShowcase
                onCtaClick={() => {
                  document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            </Center>

            <Group gap="sm" align="center" justify="center" mt={40} className="hero-grocery-mobile">
              <Text size="xs" c="dimmed" fw={500}>Works with</Text>
              <Image src="/images/kroger_integration_logo.svg" alt="Kroger" h={28} w="auto" />
              <Image src="/images/instacart_logo_large.png" alt="Instacart" h={20} w="auto" />
            </Group>
          </Grid.Col>
        </Grid>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 2: THE PROBLEM
      ────────────────────────────────────────────── */}
      <Box
        py={{ base: 40, md: 80 }}
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-gray-0) 0%, rgba(255, 250, 245, 1) 100%)'
        }}
      >
        <Container size="lg">
          <Stack gap="xl">
            <FadeIn>
              <Center>
                <Stack gap="sm" align="center" maw={650}>
                  <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                    Mealtime shouldn't be this hard
                  </Title>
                  <Text size="lg" ta="center" c="dimmed">
                    High-protein, kid-friendly, ready in 30 minutes — and that's just Monday
                  </Text>
                </Stack>
              </Center>
            </FadeIn>

            <Grid align="stretch" gutter="xl">
              {[
                {
                  icon: IconSearch,
                  title: 'The weekly grind',
                  text: <>Googling recipes, building lists, trying to make it all work. It eats your weekend and you still make the same five meals.</>
                },
                {
                  icon: IconMoodSad,
                  // nowrap: at ~320px this hyphenated compound otherwise
                  // breaks after "fits-" leaving an orphaned "none"
                  title: <span style={{ whiteSpace: 'nowrap' }}>One-size-fits-none</span>,
                  text: <>You need dairy-free <em>and</em> high-fiber <em>and</em> meal prep friendly <em>and</em> under 30 minutes. Most tools handle one of those. Real life needs all of them.</>
                },
                {
                  icon: IconTrash,
                  title: 'Food waste on repeat',
                  text: <>You buy ingredients for ambitious plans, use half of them, and throw the rest away. The cycle repeats every week.</>
                }
              ].map((card, index) => (
                <Grid.Col key={index} span={{ base: 12, sm: index === 2 ? 12 : 6, md: 4 }}>
                  <FadeIn delay={index * 0.1} style={{ height: '100%' }}>
                    <Paper p="xl" radius="lg" h="100%" style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid var(--mantine-color-orange-2)'
                    }}>
                      <Stack gap="md" align="flex-start">
                        {/* Icon sits inline with the headline at 22px, not in a chip above it:
                            Tabler is drawn for this scale, and it anchors the card without the
                            centered pastel disc this section used to use. */}
                        <Group gap="xs" align="flex-start" wrap="nowrap">
                          <card.icon
                            size={22}
                            color="var(--mantine-color-orange-6)"
                            style={{ flexShrink: 0, marginTop: 5 }}
                          />
                          <Title order={3} c="dark">
                            {card.title}
                          </Title>
                        </Group>
                        <Text c="dimmed">
                          {card.text}
                        </Text>
                      </Stack>
                    </Paper>
                  </FadeIn>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* ──────────────────────────────────────────────
          SECTION 3: THE SOLUTION
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Grid align="center" gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FadeIn>
            <Stack gap="lg" px={{ base: 'md', xl: 0 }}>
              <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
                One plan that handles all of it
              </Title>
              <Text size="lg" c="dimmed" lh={1.6}>
                Meals You Love builds personalized weekly meal plans that
                respect all of your needs at once, not just one at
                a time.
              </Text>

              <List
                spacing="sm"
                size="md"
                icon={
                  <IconCheck size={16} stroke={2.4} color="var(--mantine-color-orange-6)" />
                }
                styles={{
                  itemWrapper: {
                    alignItems: 'flex-start'
                  },
                }}
              >
                <List.Item><Text c="gray.8"><Text span fw={600}>Complex diets welcome:</Text> dairy-free + high-protein + under 30 minutes? Done.</Text></List.Item>
                <List.Item><Text c="gray.8"><Text span fw={600}>Meal prep built in:</Text> cook 3 meals, eat all week</Text></List.Item>
                <List.Item><Text c="gray.8"><Text span fw={600}>Your schedule:</Text> quick weeknight meals, bigger weekend cooks</Text></List.Item>
                <List.Item><Text c="gray.8"><Text span fw={600}>Pantry-aware:</Text> uses what you already have on hand</Text></List.Item>
                <List.Item><Text c="gray.8"><Text span fw={600}>Gets smarter over time:</Text> learns your preferences the more you use it</Text></List.Item>
              </List>
            </Stack>
            </FadeIn>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <FadeIn delay={0.15}>
              <Center>
                <Box
                  style={{
                    position: 'relative',
                    padding: '20px',
                    borderRadius: 'var(--mantine-radius-xl)',
                    background: 'linear-gradient(135deg, rgba(253, 126, 20, 0.08) 0%, rgba(250, 82, 82, 0.06) 100%)',
                  }}
                >
                  <Image
                    src="/images/meal_plan.png"
                    alt="Personalized weekly meal plan"
                    maw={460}
                    radius="lg"
                    style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)' }}
                  />
                </Box>
              </Center>
            </FadeIn>
          </Grid.Col>
        </Grid>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 4: SOCIAL PROOF
      ────────────────────────────────────────────── */}
      <SocialProof withDivider dividerColor="var(--mantine-color-orange-2)" />

      {/* ──────────────────────────────────────────────
          SECTION 5: HOW IT WORKS
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Stack gap="xl">
          <FadeIn>
            <Center>
              <Stack gap="sm" align="center" maw={600}>
                <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                  How it works
                </Title>
                <Text size="lg" ta="center" c="dimmed">
                  From your needs to dinner on the table in three steps
                </Text>
              </Stack>
            </Center>
          </FadeIn>

          <Grid align="stretch" gutter="xl">
            {[
              {
                title: 'Tell us how you eat',
                text: 'Share your dietary restrictions, taste preferences, cooking style, and family size. We\'ll take it from there and build your first plan.'
              },
              {
                title: 'Get your plan',
                text: 'We build a personalized weekly meal plan that respects all of your needs at once. Swap any recipe you don\'t love.'
              },
              {
                title: 'Shop your way',
                text: 'Add everything to your Kroger cart, send to Instacart, or download a shopping list and head to your favorite store.'
              }
            ].map((step, index) => (
              <Grid.Col key={index} span={{ base: 12, sm: index === 2 ? 12 : 6, md: 4 }}>
                <FadeIn delay={index * 0.1} style={{ height: '100%' }}>
                  <Paper p="xl" radius="lg" h="100%" ta="center" style={{
                    border: '1px solid var(--mantine-color-gray-2)'
                  }}>
                    <Stack gap="md" align="center">
                      {/* Step numeral in the brand serif, not an icon chip:
                          the numbers were already in the titles, so let them
                          be the visual. aria-hidden — the accessible order
                          comes from the list itself. */}
                      <Text
                        component="span"
                        aria-hidden="true"
                        style={{
                          fontFamily: heroFontFamily,
                          fontSize: 64,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: 'var(--mantine-color-orange-5)',
                        }}
                      >
                        {index + 1}
                      </Text>
                      <Title order={3} c="dark">
                        {step.title}
                      </Title>
                      <Text c="dimmed">
                        {step.text}
                      </Text>
                    </Stack>
                  </Paper>
                </FadeIn>
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 6: FEATURES
      ────────────────────────────────────────────── */}
      <Box
        py={{ base: 40, md: 80 }}
        style={{
          background: 'linear-gradient(45deg, var(--mantine-color-gray-0) 0%, rgba(255, 248, 240, 1) 100%)'
        }}
      >
        <Container size="lg">
          <Stack gap="xl">
            <FadeIn>
              <Center>
                <Stack gap="sm" align="center" maw={650}>
                  <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                    More than just a meal plan
                  </Title>
                  <Text size="lg" ta="center" c="dimmed">
                    Tools for every step, from planning to shopping
                  </Text>
                </Stack>
              </Center>
            </FadeIn>

            <Grid align="stretch" justify="center" gutter="xl">
              {[
                {
                  icon: IconCalendarWeek,
                  title: 'Meal prep made easy',
                  description: 'Cook 3 meals, eat all week. Batch cooking and leftovers are built into your plan so you spend less time in the kitchen.'
                },
                {
                  icon: IconChefHat,
                  title: 'Your recipes, imported',
                  description: 'Bring your own tried-and-true recipes. Import from websites, Pinterest, photos, or cookbooks and we\'ll add them to your rotation.'
                },
                {
                  icon: IconChartBar,
                  title: 'Nutrition at a glance',
                  description: 'See calories and macros for every recipe. Know exactly what you\'re eating without hunting through ingredient lists.'
                },
                {
                  icon: IconCoin,
                  title: 'Know what dinner costs',
                  description: 'See the price per serving for every meal and how it compares to takeout. Plan around your budget, not just your calendar.'
                },
                {
                  icon: IconShoppingCart,
                  title: 'Smart grocery shopping',
                  description: 'Consolidated shopping lists with nothing missing. Add to your Kroger cart for pickup or delivery, send to Instacart, or shop in-store.'
                }
              ].map((feature, index) => (
                <Grid.Col key={index} span={{ base: 12, sm: 6, lg: 4 }}>
                  <FadeIn delay={index * 0.1} style={{ height: '100%' }}>
                    <Paper p="xl" radius="lg" h="100%" ta="center" style={{
                      border: '1px solid var(--mantine-color-gray-2)'
                    }}>
                      <Stack gap="md" align="center">
                        <ThemeIcon size={60} radius="xl" color="orange" variant="light">
                          <feature.icon size={30} />
                        </ThemeIcon>
                        <Title order={4} c="dark">{feature.title}</Title>
                        <Text size="sm" c="dimmed" lh={1.5}>{feature.description}</Text>
                      </Stack>
                    </Paper>
                  </FadeIn>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* ──────────────────────────────────────────────
          SECTION 7: OUTCOMES
      ────────────────────────────────────────────── */}
      <Container size="lg" py={{ base: 40, md: 80 }}>
        <Grid align="center" gutter="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FadeIn>
            <Stack gap="lg" px={{ base: "md", xl: 0 }}>
              <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
                Stop juggling, start eating
              </Title>
              <Text size="lg" c="dimmed" lh={1.6}>
                Stop spending your weekends planning meals and your weeknights
                stressing about dinner. We'll take it from here.
              </Text>
              <List
                spacing="sm"
                size="md"
                icon={
                  <IconCheck size={16} stroke={2.4} color="var(--mantine-color-orange-6)" />
                }
                styles={{
                  itemWrapper: {
                    alignItems: 'flex-start'
                  },
                }}
              >
                <List.Item><Text c="gray.8">Save hours every week on planning and shopping</Text></List.Item>
                <List.Item><Text c="gray.8">See what each meal costs before you shop</Text></List.Item>
                <List.Item><Text c="gray.8">Waste less food with right-sized ingredient lists</Text></List.Item>
                <List.Item><Text c="gray.8">Every meal fits your whole life, not just your diet</Text></List.Item>
                <List.Item><Text c="gray.8">Discover new recipes without the endless scrolling</Text></List.Item>
              </List>
              <Box hiddenFrom="sm">
                <AuthButton size="lg" fullWidth>
                  Build your plan for free
                </AuthButton>
              </Box>
              <Box visibleFrom="sm">
                <AuthButton size="lg">
                  Build your plan for free
                </AuthButton>
              </Box>
            </Stack>
            </FadeIn>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <FadeIn delay={0.15}>
              <Center>
                <Box style={{ position: 'relative', maxWidth: '100%' }}>
                  <Image
                    src="/images/recipe-example-2.png"
                    alt="Recipe example"
                    radius="lg"
                    maw={320}
                    style={{
                      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
                      position: 'relative',
                      zIndex: 2
                    }}
                  />
                  <Image
                    src="/images/recipe-example-3.png"
                    alt="Another recipe example"
                    radius="lg"
                    maw={320}
                    className="recipe-overlap"
                    style={{
                      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.1)',
                      marginTop: '-40px',
                      marginLeft: '40px',
                      position: 'relative',
                      zIndex: 1
                    }}
                  />
                </Box>
              </Center>
            </FadeIn>
          </Grid.Col>
        </Grid>
      </Container>

      {/* ──────────────────────────────────────────────
          SECTION 8: MEAL KIT COMPARISON
      ────────────────────────────────────────────── */}
      <Box
        style={{
          background: `
            radial-gradient(56.73% 109.64% at 31.11% 87.57%, rgba(255, 146, 43, 0.08) 0%, rgba(255, 255, 255, 0) 90%),
            radial-gradient(53.27% 82.76% at 98.19% 100%, rgba(255, 99, 71, 0.06) 0%, rgba(255, 255, 255, 0) 90%),
            radial-gradient(43.01% 85.58% at 55.35% 107.63%, rgba(255, 193, 7, 0.07) 0%, rgba(255, 255, 255, 0) 90%)
          `
        }}
      >
        <Container size="lg" py={{ base: 40, md: 80 }}>
          <Stack gap="xl">
            <FadeIn>
              <Center>
                <Stack gap="sm" align="center" maw={700}>
                  <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                    Eating well shouldn't be a luxury expense
                  </Title>
                  <Text size="lg" ta="center" c="dimmed">
                    Meal kits pick for you. We plan around you.
                  </Text>
                </Stack>
              </Center>
            </FadeIn>

            <FadeIn>
            <Box style={{ position: 'relative' }}>
              <Grid gutter={{ base: 'md', sm: 'xl' }} align="stretch">
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Box p="xl" style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    height: '100%',
                    border: '1px solid var(--mantine-color-orange-3)',
                    background: 'var(--mantine-color-orange-0)'
                  }}>
                    <Stack gap="lg">
                      <Group justify="center">
                        <Image
                          src="/icon.png"
                          alt="Meals You Love"
                          w={40}
                          h={40}
                          style={{ borderRadius: 'var(--mantine-radius-xl)' }}
                        />
                        <Title order={3} c="orange.8">
                          Meals You Love
                        </Title>
                      </Group>
                      <List
                        spacing="sm"
                        size="md"
                        icon={
                          <ThemeIcon color="orange" size={24} radius="xl">
                            <IconCheck size={16} />
                          </ThemeIcon>
                        }
                        styles={{
                          itemWrapper: {
                            alignItems: 'flex-start'
                          },
                        }}
                      >
                        <List.Item><Text c="gray.8"><Text span fw={600}>Handles complex diets:</Text> combine any dietary needs, all respected at once</Text></List.Item>
                        <List.Item><Text c="gray.8"><Text span fw={600}>Your grocery store:</Text> broader selection, your brands, lower prices</Text></List.Item>
                        <List.Item><Text c="gray.8"><Text span fw={600}>True personalization:</Text> plans built around your tastes, your diet, your family</Text></List.Item>
                        <List.Item><Text c="gray.8"><Text span fw={600}>$9.99/month</Text> for personalized weekly dinner plans</Text></List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Grid.Col>

                {/* VS Element - Mobile */}
                <Grid.Col span={12} hiddenFrom="sm" style={{ marginTop: '-20px', marginBottom: '-20px', zIndex: 10 }}>
                  <Center>
                    <Box
                      style={{
                        background: 'white',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid var(--mantine-color-gray-4)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Text fw={500} c="gray.6" size="sm">vs</Text>
                    </Box>
                  </Center>
                </Grid.Col>

                {/* VS Element - Desktop */}
                <Box
                  visibleFrom="sm"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'white',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid var(--mantine-color-gray-4)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    zIndex: 10
                  }}
                >
                  <Text fw={500} c="gray.6" size="sm">vs</Text>
                </Box>

                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Box p="xl" style={{
                    borderRadius: 'var(--mantine-radius-md)',
                    height: '100%',
                    border: '1px solid var(--mantine-color-gray-2)',
                    background: 'var(--mantine-color-gray-0)'
                  }}>
                    <Stack gap="lg">
                      <Group justify="center">
                        <ThemeIcon size={40} radius="xl" color="gray" variant="light">
                          <IconPackage size={20} />
                        </ThemeIcon>
                        <Title order={3} c="gray.7">
                          Meal kit services
                        </Title>
                      </Group>
                      <List
                        spacing="sm"
                        size="md"
                        icon={
                          /* A plain muted disc, no glyph: the ✕ read as clutter at this size,
                             and the column already says "not this" through its
                             gray treatment and the ✓ column opposite it. */
                          <ThemeIcon color="gray" size={24} radius="xl" />
                        }
                        styles={{
                          itemWrapper: {
                            alignItems: 'flex-start'
                          },
                        }}
                      >
                        <List.Item><Text c="gray.6"><Text span fw={600}>Limited customization:</Text> a handful of recipes, take it or leave it</Text></List.Item>
                        <List.Item><Text c="gray.6"><Text span fw={600}>Separate from groceries:</Text> still need to shop for everything else</Text></List.Item>
                        <List.Item><Text c="gray.6"><Text span fw={600}>Mountains of packaging:</Text> ice packs, boxes, individual wrapping</Text></List.Item>
                        <List.Item><Text c="gray.6"><Text span fw={600}>$250-500+/month:</Text> costs add up fast for families</Text></List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Grid.Col>
              </Grid>
            </Box>
            </FadeIn>
          </Stack>
        </Container>

        {/* ──────────────────────────────────────────────
            SECTION 8: PRICING
        ────────────────────────────────────────────── */}
        <Container size="lg" py={{ base: 40, md: 80 }} id="pricing">
          <Stack gap="xl">
            <FadeIn>
            <Center>
              <Stack gap="sm" align="center" maw={600}>
                <Title order={2} ta="center" c="dark" style={{ fontFamily: heroFontFamily }}>
                  Pricing
                </Title>
                <Text size="lg" ta="center" c="dimmed">
                  Start with a full month free — no credit card required
                </Text>
              </Stack>
            </Center>
            </FadeIn>

            <FadeIn delay={0.1}>
            <Center>
              <Paper p={{ base: 40, md: 60 }} radius="xl" maw={500} w="100%" style={{
                border: '2px solid var(--mantine-color-orange-4)',
                position: 'relative'
              }}>
                <Box
                  style={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--mantine-color-orange-filled)',
                    color: 'white',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  1 MONTH FREE
                </Box>

                <Stack gap="lg" align="center" ta="center">
                  <Stack gap="xs" align="center">
                    <Text size="3rem" fw={700} c="dark" style={{ lineHeight: 1 }}>
                      $9.99
                    </Text>
                    <Text size="md" c="dimmed">
                      per month after trial
                    </Text>
                  </Stack>

                  <Stack gap="sm" align="flex-start" style={{ textAlign: 'left' }}>
                    <Flex gap="md" align="flex-start">
                      <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                      <Text size="md" c="gray.8">Personalized weekly meal plans</Text>
                    </Flex>
                    <Flex gap="md" align="flex-start">
                      <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                      <Text size="md" c="gray.8">All dietary restrictions supported</Text>
                    </Flex>
                    <Flex gap="md" align="flex-start">
                      <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                      <Text size="md" c="gray.8">Meal prep & batch cooking support</Text>
                    </Flex>
                    <Flex gap="md" align="flex-start">
                      <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                      <Text size="md" c="gray.8">Grocery shopping integration</Text>
                    </Flex>
                    <Flex gap="md" align="flex-start">
                      <IconCheck size={15} stroke={2.4} color="var(--mantine-color-green-7)" style={{ flexShrink: 0, marginTop: 4 }} />
                      <Text size="md" c="gray.8">Recipe import & pantry tracking</Text>
                    </Flex>
                  </Stack>

                  <AuthButton size="lg">
                    Start free trial
                  </AuthButton>

                  <Text size="sm" c="dimmed">
                    No credit card required • Cancel anytime
                  </Text>
                </Stack>
              </Paper>
            </Center>
            </FadeIn>
          </Stack>
        </Container>

        {/* ──────────────────────────────────────────────
            SECTION 9: FINAL CTA
        ────────────────────────────────────────────── */}
        <Container size="lg" py={{ base: 40, md: 80 }}>
          <FadeIn>
          <Center>
            <Stack gap="xl" align="center" ta="center" maw={900}>
              <Title order={2} c="dark" style={{ fontFamily: heroFontFamily }}>
                Ready to stop stressing about dinner?
              </Title>
              <Text size="lg" c="dimmed" maw={600}>
                Tell us how you eat. We'll handle the rest.
              </Text>
              <AuthButton size="xl">
                Build your plan for free
              </AuthButton>
              <Group gap="xs" justify="center">
                <Text size="sm" c="dimmed">
                  1-month free trial • No credit card required
                </Text>
              </Group>
            </Stack>
          </Center>
          </FadeIn>
        </Container>
      </Box>

    </Box>
  );
};
